import { useEffect, useRef, useState } from 'react';

import {
  AlertCircleIcon,
  CheckIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleUserRoundIcon,
  CopyIcon,
  HashIcon,
  MailIcon,
  PhoneIcon,
  StarIcon,
  UserIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getChannelIcon } from '@/modules/campaigns/components/Tables/CampaignsTable/ChannelCell';
import type { ContactIdentity, ContactIdentityType } from '@/shared/types/contacts';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatDate, timeFromNow } from '@/util/dateUtils';

/** `sidebar` is the human chat profile look; `compact` the Try It replay one. */
export type IdentityRowVariant = 'sidebar' | 'compact';

const identityIconMap: Record<ContactIdentityType, typeof MailIcon> = {
  email: MailIcon,
  phone: PhoneIcon,
  psid: UserIcon,
  external_id: HashIcon,
};

const compactIconMap: Record<ContactIdentityType, typeof MailIcon> = {
  ...identityIconMap,
  psid: CircleUserRoundIcon,
};

const knownChannels = new Set<string>(Object.values(IntegrationType));

export const isKnownChannel = (channel?: string | null): channel is string =>
  !!channel && knownChannels.has(channel);

/** Copy button with inline check feedback instead of the atom's toast. */
export const InlineCopyButton = ({ value, className }: { value: string; className?: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    // Restart the countdown, or a second copy inherits the first one's remainder.
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost-neutral"
      size="icon-sm"
      onClick={handleCopy}
      aria-label={t('common.copy')}
      className={cn('size-6', className)}
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </Button>
  );
};

const IdentityTooltipContent = ({
  identity,
  variant,
}: {
  identity: ContactIdentity;
  variant: IdentityRowVariant;
}) => {
  const { t } = useTranslation();
  const verifiedOn = identity.verified_at ? formatDate(identity.verified_at, 'MMM d, yyyy') : null;
  const verifiedLine = (() => {
    if (!identity.verified) {
      return null;
    }
    if (identity.verified_source && verifiedOn) {
      return t('contacts.verified_source_line', { 0: identity.verified_source, 1: verifiedOn });
    }
    if (identity.verified_source) {
      return t('contacts.verified_via', { 0: identity.verified_source });
    }
    if (verifiedOn) {
      return t('contacts.verified_on', { 0: verifiedOn });
    }
    return t('contacts.verified');
  })();

  return (
    <div className="flex min-w-56 flex-col gap-1.5 px-3 py-2">
      <div className="flex items-center justify-between gap-3 pb-1">
        <span className="truncate text-xs font-semibold">{identity.value}</span>
        <span className="shrink-0 text-xs text-primary-foreground/70">
          {timeFromNow(identity.updated)}
        </span>
      </div>
      {verifiedLine && (
        <div className="flex items-center gap-2">
          <CircleCheckIcon size={12} className="shrink-0 text-success" />
          <span className="text-xs">{verifiedLine}</span>
        </div>
      )}
      {/* The sidebar shows nothing for an unverified identity. */}
      {!verifiedLine && variant === 'compact' && (
        <div className="flex items-center gap-2">
          <AlertCircleIcon size={12} className="shrink-0 text-warning" />
          <span className="text-xs">{t('contacts.not_verified')}</span>
        </div>
      )}
      {isKnownChannel(identity.channel) && (
        <div className="flex items-center gap-2">
          <span className="flex shrink-0 items-center">{getChannelIcon(identity.channel, 12)}</span>
          <span className="text-xs">{t(getChannelKey(identity.channel as IntegrationType))}</span>
        </div>
      )}
      {!identity.wrong_party && (
        <div className="flex items-center gap-2">
          <UserIcon size={12} className="shrink-0 text-primary-foreground/70" />
          <span className="text-xs">{t('contacts.matches_contact')}</span>
        </div>
      )}
    </div>
  );
};

type Props = {
  identity: ContactIdentity;
  /** Marks the contact's primary email/phone with a star. */
  primary?: boolean;
  variant?: IdentityRowVariant;
};

/**
 * One contact identity (email / phone / psid / external id): channel icon, value,
 * copy on hover and verified state, with the provenance in a tooltip.
 */
export const IdentityRow = ({ identity, primary = false, variant = 'sidebar' }: Props) => {
  const { t } = useTranslation();
  const isCompact = variant === 'compact';
  const iconSize = isCompact ? 16 : 14;
  const iconMap = isCompact ? compactIconMap : identityIconMap;
  const Icon = iconMap[identity.type] ?? iconMap.psid;
  const leadingIcon = isKnownChannel(identity.channel) ? (
    <span className="flex shrink-0 items-center">{getChannelIcon(identity.channel, iconSize)}</span>
  ) : (
    <Icon size={iconSize} className="shrink-0" />
  );

  const trailingIcon = (() => {
    if (identity.verified) {
      return <CircleCheckIcon size={16} className="shrink-0 text-success" />;
    }
    if (isCompact) {
      return <CircleDashedIcon size={16} className="shrink-0 text-muted-foreground" />;
    }
    return <CircleCheckIcon size={16} className="shrink-0 opacity-40" />;
  })();

  const row = (
    <div
      className={cn(
        'group flex items-center gap-2 px-2 py-1.5 transition-colors',
        isCompact
          ? 'min-w-32 self-stretch rounded text-foreground hover:bg-selected-soft'
          : cn('rounded-md hover:bg-muted/50', !identity.verified && 'text-muted-foreground')
      )}
    >
      {leadingIcon}
      {/* Compact hugs the value so copy sits next to it; sidebar pushes it to the end. */}
      <span className={cn('min-w-0 truncate text-sm', !isCompact && 'flex-1')}>
        {identity.value}
      </span>
      {isCompact ? (
        <CopyButton
          data={identity.value}
          buttonSize="flush"
          className="flex size-4 shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 hover:[&_svg]:stroke-foreground hover:[&_svg]:text-foreground"
        />
      ) : (
        <span className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <InlineCopyButton value={identity.value} />
        </span>
      )}
      <span className={cn('flex shrink-0 items-center gap-2', isCompact && 'ml-auto')}>
        {primary && (
          <StarIcon
            size={16}
            strokeWidth={1.25}
            className="fill-muted-foreground text-muted-foreground"
            aria-label={t('contacts.primary')}
          />
        )}
        {trailingIcon}
      </span>
    </div>
  );

  return (
    <InfoTooltip
      content={<IdentityTooltipContent identity={identity} variant={variant} />}
      variant="rich"
      size="auto"
      side="left"
      showArrow={false}
    >
      {row}
    </InfoTooltip>
  );
};
